import { useQuery } from '@tanstack/react-query';
import { ListSuperAdminUsersResponse, listSuperAdminUsersContract } from '@bliks-io/contracts';
import { apiClient } from '@/api/client';

/**
 * Query key factory for superadmin user-related queries
 */
export const superAdminUserKeys = {
  all: ['superadmin-users'] as const,
  lists: () => [...superAdminUserKeys.all, 'list'] as const,
  list: () => [...superAdminUserKeys.lists()] as const,
  detail: (userId: string) => [...superAdminUserKeys.all, userId] as const
};

/**
 * List SuperAdmin users query
 *
 * Fetches all superadmin users in the system (super-admin only).
 * Returns an array of superadmin users with their id, name, email, status, and timestamps.
 *
 * @example
 * ```tsx
 * function UserListPage() {
 *   const { data, isLoading, error } = useListSuperAdminUsers();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error loading users</div>;
 *
 *   return (
 *     <ul>
 *       {data?.map(user => (
 *         <li key={user._id}>{user.email}</li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useListSuperAdminUsers() {
  return useQuery({
    queryKey: superAdminUserKeys.list(),
    queryFn: async ({ signal }): Promise<ListSuperAdminUsersResponse> => {
      const { data } = await apiClient.get<ListSuperAdminUsersResponse>(
        listSuperAdminUsersContract.path,
        { signal }
      );
      return data;
    }
  });
}
